import React from 'react';
import { Users, MessageCircle, Sparkles } from 'lucide-react';

const ExosomeSection = () => {
  const features = [
    {
      icon: Sparkles,
      title: 'Autologous Exosomes',
      description: 'Naturally derived from your own cells, ensuring biocompatibility and eliminating rejection risks'
    },
    {
      icon: MessageCircle,
      title: 'Cell Communication',
      description: 'Microscopic messengers carrying proteins, lipids, and genetic material for intercellular signaling'
    },
    {
      icon: Users,
      title: 'Regeneration',
      description: 'Activates tissue repair pathways, accelerates healing, and enhances regenerative capacity'
    }
  ];

  return (
    <section className="relative py-32 px-6 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 overflow-hidden">
      {/* Animated particle background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Flowing particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-teal-400/30 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          ></div>
        ))}
        
        {/* Larger glowing orbs */}
        {[...Array(8)].map((_, i) => (
          <div
            key={`orb-${i}`}
            className="absolute bg-teal-500/10 rounded-full blur-2xl animate-float"
            style={{
              width: `${100 + Math.random() * 200}px`,
              height: `${100 + Math.random() * 200}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${5 + Math.random() * 5}s`
            }}
          ></div>
        ))}

        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full opacity-20">
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <line x1="10%" y1="20%" x2="90%" y2="80%" stroke="url(#lineGradient)" strokeWidth="1" className="animate-pulse" />
          <line x1="20%" y1="80%" x2="80%" y2="20%" stroke="url(#lineGradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '1s' }} />
          <line x1="50%" y1="10%" x2="50%" y2="90%" stroke="url(#lineGradient)" strokeWidth="1" className="animate-pulse" style={{ animationDelay: '2s' }} />
        </svg>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20 animate-fade-in">
          <h2 className="text-6xl md:text-7xl font-light mb-6 text-white">
            Exosome <span className="text-teal-400 font-light">Revolution</span>
          </h2>
          <p className="text-teal-200 text-xl font-light max-w-3xl mx-auto leading-relaxed">
            Harnessing the power of naturally occurring cellular messengers for unprecedented regenerative outcomes
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-10 mb-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={index}
                className="group relative animate-fade-in"
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Card glow effect */}
                <div className="absolute inset-0 bg-teal-500/10 rounded-3xl blur-xl group-hover:bg-teal-500/20 transition-all duration-500"></div>
                
                {/* Card content */}
                <div className="relative p-10 bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 hover:border-teal-400/30 transition-all duration-500 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-400/20 to-teal-600/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <Icon className="w-8 h-8 text-teal-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-light mb-4 text-white group-hover:text-teal-300 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-teal-200/80 font-light leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-8 animate-fade-in">
          <a
            href="/exosomes"
            className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white px-12 py-5 rounded-2xl font-light transition-all duration-300 shadow-soft-xl hover:shadow-teal-500/50 hover:scale-[1.02]"
          >
            Discover Exosome Technology
          </a>
        </div>
      </div>
    </section>
  );
};

export default ExosomeSection;
